import { HTMLAttributes, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import type { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import { AuditLogsAccessResponse } from 'api';
import { AUDIT_LOGS_TEST_IDS } from 'pages/AuditLogs/AuditLogs.testIds';
import { CHILDREN_BUSINESS_UNIT, FIRM_KIND, STANDALONE_KIND, TOP_LEVEL_BUSINESS_UNIT } from 'pages/AuditLogs/constants';
import { FlatOption, ProductSelection } from 'pages/AuditLogs/types';
import { filterOptionsByProduct, getOptionsFromScope, isSelectionValidForProduct } from 'pages/AuditLogs/utilities';
import GroupHeaderContent from './GroupHeaderContent';
import VirtualizedListbox from './VirtualizedListbox';

const BusinessUnitSelect = ({
  scope,
  productSelection,
  handleBusinessEntitySelection,
}: {
  scope: AuditLogsAccessResponse;
  productSelection: ProductSelection;
  handleBusinessEntitySelection: (selection: FlatOption | null) => void;
}) => {
  const allOptions = useMemo(() => getOptionsFromScope(scope), [scope]);

  // Filter options based on product selection
  const options = useMemo(() => filterOptionsByProduct(allOptions, productSelection), [allOptions, productSelection]);
  const firmOptionByGroupKey = useMemo(() => {
    const map = new Map<string, FlatOption>();
    options.forEach((opt: FlatOption) => {
      if (opt.kind === FIRM_KIND) {
        map.set(opt.value, opt);
      }
    });
    return map;
  }, [options]);

  const [value, setValue] = useState<FlatOption | null>(null);
  const firstSelectionHappened = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onChangeHandler = useCallback((_event: SyntheticEvent, selected: FlatOption | null) => {
    firstSelectionHappened.current = true;
    setValue(selected);
  }, []);

  // Clear selection if it becomes invalid for the current product
  useEffect(() => {
    if (value && !isSelectionValidForProduct(value, productSelection)) {
      setValue(null);
      if (firstSelectionHappened.current) {
        handleBusinessEntitySelection(null);
      }
    }
  }, [value, productSelection, handleBusinessEntitySelection]);

  useEffect(() => {
    if (firstSelectionHappened.current) {
      handleBusinessEntitySelection(value);
    }
  }, [value, handleBusinessEntitySelection]);

  const groupBy = useCallback((option: FlatOption) => {
    if (option.kind === STANDALONE_KIND) {
      return ''; // No grouping for standalone
    }
    if (option.kind === FIRM_KIND) {
      return option.value; // Group firms by their own value
    }
    // For fund and company, group them under their parent firm
    return option.parentValue || '';
  }, []);

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const { group, children: groupChildren } = params;

      if (!group) {
        return <div key="no-group">{groupChildren}</div>;
      }

      const groupOption = firmOptionByGroupKey.get(group);

      return (
        <div key={group}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'white',
              borderBottom: '1px solid #e0e0e0',
            }}>
            {groupOption && (
              <GroupHeaderContent
                option={groupOption}
                onClick={() => {
                  firstSelectionHappened.current = true;
                  setValue(groupOption);
                  inputRef.current?.blur();
                }}
              />
            )}
          </Box>
          {groupChildren}
        </div>
      );
    },
    [firmOptionByGroupKey]
  );

  const renderOption = useCallback((props: HTMLAttributes<HTMLLIElement>, option: FlatOption) => {
    if (option.kind === FIRM_KIND) {
      return null;
    }

    const paddingLeft = CHILDREN_BUSINESS_UNIT.has(option.kind) ? '20px' : '4px';
    const fontWeight = TOP_LEVEL_BUSINESS_UNIT.has(option.kind) ? 600 : 400;

    return (
      <li {...props} key={option.value}>
        <Box
          component="span"
          title={option.label}
          sx={{
            display: 'block',
            pl: paddingLeft,
            fontWeight,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer',
          }}>
          {option.label}
        </Box>
      </li>
    );
  }, []);

  return (
    <Box sx={{ width: 320 }}>
      <Autocomplete<FlatOption, false, false, false>
        options={options}
        value={value}
        onChange={onChangeHandler}
        groupBy={groupBy}
        renderGroup={renderGroup}
        renderOption={renderOption}
        renderInput={params => <TextField {...params} inputRef={inputRef} placeholder="Select a business unit" data-testid={AUDIT_LOGS_TEST_IDS.businessUnitInput}/>}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        slots={{ listbox: VirtualizedListbox }}
        data-testid={AUDIT_LOGS_TEST_IDS.businessUnitSelect}
      />
    </Box>
  );
};

export default BusinessUnitSelect;
